import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Button, TextInput } from "flowbite-react";
import {
  HiMail,
  HiPhone,
  HiCalendar,
  HiIdentification,
  HiLocationMarker,
  HiAcademicCap,
  HiUserCircle,
  HiCog,
  HiExclamationCircle,
  HiPencilAlt,
  HiShieldCheck,
  HiLockClosed,
  HiExclamation,
  HiCheck,
  HiX,
  HiCurrencyDollar,
} from "react-icons/hi";
import {
  changeUserPlan,
  updateUserTokens,
  addUserTokens,
  subtractUserTokens,
} from "../api/adminService";

const UserProfileDetailModal = ({
  isOpen,
  onClose,
  selectedUser,
  updateUserRole,
  toggleUserStatus,
  setIsDeleteModalOpen,
  setIsUserModalOpen,
  getUserDisplayName,
  getUserInitial,
  getUserPlan,
  getUserTokens,
  setToastMessage,
  setShowToast,
  handleUpdates,
  setSelectedUser,
}) => {
  const [activeSection, setActiveSection] = useState("userInfo");
  const [isEditingTokens, setIsEditingTokens] = useState(false);
  const [tokenValue, setTokenValue] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [isRoleChanged, setIsRoleChanged] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPlanChanged, setIsPlanChanged] = useState(false);
  const [tokenActionType, setTokenActionType] = useState(null);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Reset modal state when it closes or when selectedUser changes
  useEffect(() => {
    if (!isOpen) {
      // Reset to default state when modal closes
      setActiveSection("userInfo");
      setIsEditingTokens(false);
      setTokenValue("");
      setSelectedRole(null);
      setIsRoleChanged(false);
      setSelectedPlan(null);
      setIsPlanChanged(false);
      setTokenActionType(null);
      setShowResetConfirmation(false);
    }
  }, [isOpen, selectedUser]);

  if (!selectedUser) return null;

  // Format date to dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get user role from accessLevel
  const getUserRole = (user) => {
    if (user.accessLevel === 0) return "SuperAdmin";
    if (user.accessLevel === 1) return "CRMAdmin";
    if (user.accessLevel === 2) return "APIManager";
    if (user.accessLevel === 3) return "SupportCoordinator";
    return "User";
  };

  // Handle token edit save
  const handleTokenEditSave = async () => {
    if (!selectedUser || isNaN(parseInt(tokenValue, 10))) {
      return;
    }

    try {
      const email = selectedUser.email;
      const tokens = parseInt(tokenValue, 10);
      let response;

      switch (tokenActionType) {
        case "edit":
          response = await updateUserTokens(email, tokens);
          break;
        case "add":
          response = await addUserTokens(email, tokens);
          break;
        case "remove":
          response = await subtractUserTokens(email, tokens);
          break;
        case "reset":
          response = await updateUserTokens(email, 0);
          break;
        default:
          console.error("Unknown token action type:", tokenActionType);
          return;
      }

      // Update the local state for immediate UI feedback
      if (response && response.success) {
        // For edit and reset, we directly set the token value
        if (tokenActionType === "edit" || tokenActionType === "reset") {
          selectedUser.tokens = tokenActionType === "reset" ? 0 : tokens;
        }
        // For add and remove, we update based on the response from the server
        else if (response.updatedTokens !== undefined) {
          selectedUser.tokens = response.updatedTokens;
        } else if (tokenActionType === "add") {
          selectedUser.tokens = (selectedUser.tokens || 0) + tokens;
        } else if (tokenActionType === "remove") {
          selectedUser.tokens = Math.max(
            0,
            (selectedUser.tokens || 0) - tokens
          );
        }

        // Show success notification
        if (setToastMessage && setShowToast) {
          const actionText =
            tokenActionType === "edit"
              ? "updated"
              : tokenActionType === "add"
              ? "added to"
              : tokenActionType === "remove"
              ? "removed from"
              : "reset to 0 for";

          setToastMessage(
            `Tokens ${actionText} ${getUserDisplayName(
              selectedUser
            )}'s account successfully`
          );
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
        }

        // Update the UI if needed
        if (handleUpdates) {
          handleUpdates();
        }
      }

      // Force a re-render by creating a new selectedUser object
      const updatedUser = { ...selectedUser };
      // This is a hack to force the component to re-render with the updated token value
      setTimeout(() => {
        if (setSelectedUser) {
          setSelectedUser(updatedUser);
        }
      }, 0);
    } catch (error) {
      // Show error notification
      if (setToastMessage && setShowToast) {
        setToastMessage(`Failed to update tokens: ${error.message}`);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
      }
      console.error("Token update error:", error);
    }

    // Reset state
    setIsEditingTokens(false);
    setTokenActionType(null);
    setShowResetConfirmation(false);
  };

  const handleTokenAction = (actionType) => {
    if (actionType === "reset") {
      setTokenActionType("reset");
      setShowResetConfirmation(true);
      return;
    }

    let initialValue = "";

    switch (actionType) {
      case "edit":
        initialValue = getUserTokens(selectedUser).toString();
        break;
      case "add":
      case "remove":
        initialValue = "";
        break;
      default:
        initialValue = "";
    }

    setTokenValue(initialValue);
    setTokenActionType(actionType);
    setIsEditingTokens(true);
  };

  const handleConfirmReset = () => {
    setShowResetConfirmation(false);
    handleTokenEditSave(); // This will call updateUserTokens with tokens=0
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setIsPlanChanged(true);
  };

  // Handle save plan changes
  const handleSavePlanChanges = () => {
    if (selectedPlan) {
      // Convert plan name to plan ID
      let planId;
      switch (selectedPlan) {
        case "Trial":
          planId = 0;
          break;
        case "Developer":
          planId = 1;
          break;
        case "Enterprise":
          planId = 2;
          break;
        case "Educational":
          planId = 3;
          break;
        default:
          planId = 0; // Default to Trial
      }

      // Call the API to update the plan
      changeUserPlan(selectedUser.email, planId)
        .then((response) => {
          // Update the local user state
          selectedUser.planId = planId;
          selectedUser.plan = planId; // Update both properties for consistency

          // Show toast notification
          if (setToastMessage && setShowToast) {
            setToastMessage(
              `User plan updated to ${selectedPlan} successfully`
            );
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
          }

          // Update the UI
          if (handleUpdates) {
            handleUpdates();
          }

          // Reset state
          setSelectedPlan(null);
          setIsPlanChanged(false);
        })
        .catch((error) => {
          // Show error notification
          if (setToastMessage && setShowToast) {
            setToastMessage(`Failed to update plan: ${error.message}`);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000); // Auto-dismiss after 3 seconds
          }
        });
    }
  };

  // Handle cancel plan changes
  const handleCancelPlanChanges = () => {
    setIsPlanChanged(false);
    setSelectedPlan(null);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setIsRoleChanged(true);
  };

  // Handle save role changes
  const handleSaveRoleChanges = () => {
    if (selectedRole) {
      // Call the updateUserRole with the correct role string
      if (typeof updateUserRole === "function") {
        updateUserRole(selectedUser._id, selectedRole, true);

        // Update the local state for immediate UI feedback
        let accessLevel;
        switch (selectedRole) {
          case "crm":
            accessLevel = 1;
            break;
          case "api":
            accessLevel = 2;
            break;
          case "moderator":
            accessLevel = 3;
            break;
          case "user":
            accessLevel = 4;
            break;
          default:
            accessLevel = 4;
        }
        selectedUser.accessLevel = accessLevel;
      }
      setIsRoleChanged(false);
      setSelectedRole(null);
    }
  };

  // Handle cancel role changes
  const handleCancelRoleChanges = () => {
    setIsRoleChanged(false);
    setSelectedRole(null);
  };

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      size="5xl"
      position="center"
      popup
      theme={{
        root: {
          base: "fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          show: {
            on: "flex bg-gray-900 bg-opacity-80 dark:bg-opacity-90 backdrop-blur-sm",
            off: "hidden",
          },
        },
        content: {
          base: "relative h-full w-full p-4 md:h-auto",
          inner:
            "relative rounded-2xl bg-[#E8ECDA] shadow-2xl flex flex-col h-[90vh] md:h-[560px] w-full md:w-[1040px] max-w-7xl mx-auto overflow-hidden",
        },
      }}
    >
      <ModalHeader
        className="px-4 py-2 border-none bg-[#E8ECDA]"
        theme={{
          close: {
            base: "ml-auto inline-flex items-center rounded-full bg-black/5 p-2 text-sm text-[#5C6353] hover:bg-black/10 hover:text-[#161616]",
          },
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-[#161616] text-white text-sm font-semibold hover:bg-[#232323]"
            aria-label="Back to admin"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
          <span className="sr-only">User details</span>
        </div>
      </ModalHeader>
      <ModalBody className="p-0 overflow-hidden">
        <div className="flex h-full w-full p-6 gap-6">
          {/* Left sidebar */}
          <div className="hidden md:flex w-72 bg-[#161616] rounded-2xl p-5 flex-col">
            {/* Profile section */}
            <div className="flex items-center gap-3 mb-6">
              <div className="relative">
                <div className="h-14 w-14 rounded-full overflow-hidden flex items-center justify-center bg-[#E8ECDA] text-[#161616] text-xl font-bold">
                  {selectedUser.imageUrl ? (
                    <img
                      src={selectedUser.imageUrl}
                      alt={getUserDisplayName(selectedUser)}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    getUserInitial(selectedUser).toUpperCase()
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm truncate">
                  {getUserDisplayName(selectedUser)}
                </div>
                <div className="text-white/60 text-xs truncate">
                  {selectedUser.email}
                </div>
                <div className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-white/80">
                  {getUserRole(selectedUser)}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex flex-col gap-2">
              <button
                onClick={() => setActiveSection("userInfo")}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-left transition-colors ${
                  activeSection === "userInfo"
                    ? "bg-[#BDE958] text-[#161616]"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <HiUserCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">User Information</span>
              </button>
              <button
                onClick={() => setActiveSection("roleManagement")}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-left transition-colors ${
                  activeSection === "roleManagement"
                    ? "bg-[#BDE958] text-[#161616]"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <HiShieldCheck className="w-5 h-5" />
                <span className="text-sm font-semibold">Role Management</span>
              </button>
              <button
                onClick={() => setActiveSection("tokenManagement")}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-left transition-colors ${
                  activeSection === "tokenManagement"
                    ? "bg-[#BDE958] text-[#161616]"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <HiCurrencyDollar className="w-5 h-5" />
                <span className="text-sm font-semibold">Token Management</span>
              </button>
              <button
                onClick={() => setActiveSection("subscriptionPlan")}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-left transition-colors ${
                  activeSection === "subscriptionPlan"
                    ? "bg-[#BDE958] text-[#161616]"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <HiAcademicCap className="w-5 h-5" />
                <span className="text-sm font-semibold">Subscription</span>
              </button>
              <button
                onClick={() => setActiveSection("accountAction")}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-left transition-colors ${
                  activeSection === "accountAction"
                    ? "bg-[#BDE958] text-[#161616]"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <HiExclamation className="w-5 h-5" />
                <span className="text-sm font-semibold">Account Action</span>
              </button>
            </nav>

            <div className="mt-auto pt-4" />
          </div>

          {/* Mobile section tabs */}
          <div className="flex md:hidden overflow-x-auto gap-2 pb-3 -mt-2 flex-shrink-0">
            {[
              { key: "userInfo", label: "Info", icon: <HiUserCircle className="w-4 h-4" /> },
              { key: "roleManagement", label: "Role", icon: <HiShieldCheck className="w-4 h-4" /> },
              { key: "tokenManagement", label: "Tokens", icon: <HiCurrencyDollar className="w-4 h-4" /> },
              { key: "subscriptionPlan", label: "Plan", icon: <HiAcademicCap className="w-4 h-4" /> },
              { key: "accountAction", label: "Action", icon: <HiExclamation className="w-4 h-4" /> },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                  activeSection === tab.key
                    ? "bg-[#BDE958] text-[#161616]"
                    : "bg-[#161616] text-white/80 hover:bg-[#232323]"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Right content */}
          <div className="flex-1 overflow-y-auto pr-2">
            {/* User Information Section */}
            {activeSection === "userInfo" && (
              <div className="space-y-6">
                <h2 className="text-2xl md:text-4xl font-extrabold text-[#161616] mb-2">
                  User Information
                </h2>

                {/* Account Information */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold mb-4">
                    ACCOUNT INFORMATION
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiIdentification className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          User ID
                        </p>
                        <p className="text-sm text-[#161616] break-words max-w-full pr-4">
                          {selectedUser._id ? selectedUser._id : "N/A"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiAcademicCap className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Subscription Plan
                        </p>
                        <p className="text-sm text-[#161616]">
                          {getUserPlan(selectedUser)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiCalendar className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Joined Date
                        </p>
                        <p className="text-sm text-[#161616]">
                          {formatDate(selectedUser.joiningDate)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiExclamationCircle className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Status
                        </p>
                        <p className="text-sm text-[#161616]">
                          {selectedUser.verified ? "Verified" : "Not Verified"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold mb-4">
                    CONTACT
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiMail className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Email Address
                        </p>
                        <p className="text-sm text-[#161616]">
                          {selectedUser.email || "Not provided"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiPhone className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Phone
                        </p>
                        <p className="text-sm text-[#161616]">
                          {selectedUser.phone || "Not provided"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 text-[#5C6353] mt-0.5">
                        <HiLocationMarker className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-[#8C9086]">
                          Location
                        </p>
                        <p className="text-sm text-[#161616]">
                          {selectedUser.location || "Not provided"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Role Management Section */}
            {activeSection === "roleManagement" && (
              <div className="space-y-6">
                <h2 className="text-2xl md:text-4xl font-extrabold text-[#161616] mb-2">
                  Role Management
                </h2>

                {/* User Role Management with updated colors and save/cancel buttons */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold mb-4">
                    USER ROLE
                  </h4>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(() => {
                      const isActive = (key, accessLevelMatch) =>
                        selectedRole === key || (!selectedRole && accessLevelMatch);

                      const tileBase =
                        "rounded-xl border bg-[#232323] text-white px-4 py-4 cursor-pointer transition-colors hover:bg-[#2A2B2D]";
                      const tileActive =
                        "border-[#BDE958] bg-[#BDE958]/20 text-[#161616] ring-2 ring-[#BDE958]";

                      return (
                        <>
                          <div
                            onClick={() => handleRoleSelect("crm")}
                            className={`${tileBase} ${
                              isActive("crm", selectedUser.accessLevel === 1) ? tileActive : "border-[#232323]"
                            }`}
                          >
                            <div className="flex flex-col items-center text-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-purple-200/30 flex items-center justify-center">
                                <HiShieldCheck className="h-5 w-5 text-purple-200" />
                              </div>
                              <div className="text-sm font-semibold">CRM Admin</div>
                            </div>
                          </div>

                          <div
                            onClick={() => handleRoleSelect("api")}
                            className={`${tileBase} ${
                              isActive("api", selectedUser.accessLevel === 2) ? tileActive : "border-[#232323]"
                            }`}
                          >
                            <div className="flex flex-col items-center text-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-blue-200/30 flex items-center justify-center">
                                <HiShieldCheck className="h-5 w-5 text-blue-200" />
                              </div>
                              <div className="text-sm font-semibold">API Manager</div>
                            </div>
                          </div>

                          <div
                            onClick={() => handleRoleSelect("moderator")}
                            className={`${tileBase} ${
                              isActive("moderator", selectedUser.accessLevel === 3) ? tileActive : "border-[#232323]"
                            }`}
                          >
                            <div className="flex flex-col items-center text-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-emerald-200/30 flex items-center justify-center">
                                <HiShieldCheck className="h-5 w-5 text-emerald-200" />
                              </div>
                              <div className="text-sm font-semibold">Support Coordinator</div>
                            </div>
                          </div>

                          <div
                            onClick={() => handleRoleSelect("user")}
                            className={`${tileBase} ${
                              isActive(
                                "user",
                                selectedUser.accessLevel === 4 || selectedUser.accessLevel === undefined
                              )
                                ? tileActive
                                : "border-[#232323]"
                            }`}
                          >
                            <div className="flex flex-col items-center text-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gray-200/30 flex items-center justify-center">
                                <HiUserCircle className="h-5 w-5 text-gray-200" />
                              </div>
                              <div className="text-sm font-semibold">Regular User</div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Save/Cancel buttons for role changes - Note: Order switched as requested */}
                  {isRoleChanged && (
                    <div className="flex justify-end mt-5 gap-3">
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleSaveRoleChanges}
                        className="bg-[#BDE958] hover:bg-[#cbf176] text-[#161616] border-none"
                      >
                        <HiCheck className="mr-2 h-4 w-4" />
                        Save Changes
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        onClick={handleCancelRoleChanges}
                        className="bg-[#E8ECDA] hover:bg-[#DEE3D0] text-[#161616] border-none"
                      >
                        <HiX className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Token Management Section */}
            {activeSection === "tokenManagement" && (
              <div className="space-y-6">
                <h2 className="text-2xl md:text-4xl font-extrabold text-[#161616] mb-2">
                  Token Management
                </h2>

                {/* Token Management */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold">USER TOKENS</h4>

                  <div className="bg-[#E8ECDA] rounded-2xl p-4 md:p-6 mt-4 border border-black/5 min-h-[180px]">
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <div className="text-sm font-semibold text-[#161616]">Current Token Balance</div>
                        <div className="text-xs text-[#8C9086] mt-1">Tokens are used for API calls and other services</div>
                      </div>
                      <div className="text-3xl font-extrabold text-[#161616]">
                        {getUserTokens(selectedUser).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h5 className="uppercase text-[#5C6353] text-xs font-bold mb-4">TOKEN ACTIONS</h5>

                      {isEditingTokens ? (
                        <div className="bg-[#E8ECDA] rounded-2xl p-5 border border-black/5">
                          <div className="mb-4">
                            <label className="block text-sm font-semibold text-[#161616] mb-2">
                              {tokenActionType === "edit"
                                ? "New Token Balance"
                                : tokenActionType === "add"
                                ? "Amount to Add"
                                : tokenActionType === "remove"
                                ? "Amount to Remove"
                                : "Reset to Value"}
                            </label>
                            <TextInput
                              type="number"
                              value={tokenValue}
                              onChange={(e) => setTokenValue(e.target.value)}
                              placeholder={
                                tokenActionType === "edit"
                                  ? "Enter new balance"
                                  : "Enter amount"
                              }
                              className="w-full"
                            />
                            <p className="mt-2 text-xs text-[#8C9086]">
                              {tokenActionType === "edit"
                                ? "This will replace the current token balance"
                                : tokenActionType === "add"
                                ? "This amount will be added to the current balance"
                                : tokenActionType === "remove"
                                ? "This amount will be deducted from the current balance"
                                : ""}
                            </p>
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleTokenEditSave}
                              className="bg-[#BDE958] hover:bg-[#cbf176] text-[#161616] border-none"
                            >
                              <HiCheck className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => {
                                setIsEditingTokens(false);
                                setTokenActionType(null);
                              }}
                              className="bg-[#E8ECDA] hover:bg-[#DEE3D0] text-[#161616] border-none"
                            >
                              <HiX className="mr-2 h-4 w-4" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : showResetConfirmation ? (
                        <div className="bg-[#E8ECDA] rounded-2xl p-5 border border-black/5">
                          <div className="flex items-start mb-3">
                            <div className="flex-shrink-0">
                              <HiExclamationCircle className="h-6 w-6 text-yellow-700" />
                            </div>
                            <div className="ml-3">
                              <h6 className="text-sm font-semibold text-[#161616] mb-1">
                                Confirm Token Reset
                              </h6>
                              <p className="text-sm text-[#5C6353]">
                                Are you sure you want to reset the token count
                                to 0 for this user? This action cannot be
                                undone.
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-end gap-3 mt-4">
                            <Button
                              color="warning"
                              size="sm"
                              onClick={handleConfirmReset}
                              className="bg-red-600 hover:bg-red-700 text-white border-none"
                            >
                              <HiCheck className="mr-2 h-4 w-4" />
                              Confirm Reset
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => {
                                setShowResetConfirmation(false);
                                setTokenActionType(null);
                              }}
                              className="bg-[#E8ECDA] hover:bg-[#DEE3D0] text-[#161616] border-none"
                            >
                              <HiX className="mr-2 h-4 w-4" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <button
                            onClick={() => handleTokenAction("edit")}
                            className="group bg-[#232323] text-white border border-[#232323] rounded-xl p-4 cursor-pointer hover:bg-[#2A2B2D] transition-colors"
                          >
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                                <HiPencilAlt className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <span className="block text-sm font-semibold mb-1">
                                  Edit
                                </span>
                                <span className="block text-xs text-white/70">
                                  Set new balance
                                </span>
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={() => handleTokenAction("add")}
                            className="group bg-[#232323] text-white border border-[#232323] rounded-xl p-4 cursor-pointer hover:bg-[#2A2B2D] transition-colors"
                          >
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-6 h-6 text-white"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 4.5v15m7.5-7.5h-15"
                                  />
                                </svg>
                              </div>
                              <div>
                                <span className="block text-sm font-semibold mb-1">
                                  Add
                                </span>
                                <span className="block text-xs text-white/70">
                                  Increase tokens
                                </span>
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={() => handleTokenAction("remove")}
                            className="group bg-[#232323] text-white border border-[#232323] rounded-xl p-4 cursor-pointer hover:bg-[#2A2B2D] transition-colors"
                          >
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-6 h-6 text-white"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 12h-15"
                                  />
                                </svg>
                              </div>
                              <div>
                                <span className="block text-sm font-semibold mb-1">
                                  Remove
                                </span>
                                <span className="block text-xs text-white/70">
                                  Decrease tokens
                                </span>
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={() => handleTokenAction("reset")}
                            className="group bg-gray-700 text-white border border-gray-600 rounded-xl p-4 cursor-pointer hover:bg-gray-600 transition-colors"
                          >
                            <div className="flex flex-col items-center text-center space-y-3">
                              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-6 h-6 text-white"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                                  />
                                </svg>
                              </div>
                              <div>
                                <span className="block text-sm font-semibold mb-1">
                                  Reset
                                </span>
                                <span className="block text-xs text-white/70">
                                  Clear all tokens
                                </span>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Plan Section */}
            {activeSection === "subscriptionPlan" && (
              <div className="space-y-6">
                <h2 className="text-2xl md:text-4xl font-extrabold text-[#161616] mb-2">Subscription</h2>

                {/* Subscription Plans */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold mb-4">AVAILABLE PLANS</h4>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Enterprise Plan */}
                    <div
                      onClick={() => handlePlanSelect("Enterprise")}
                      className={`rounded-lg border p-3 cursor-pointer transition-all ${
                        selectedPlan === "Enterprise" ||
                        (!selectedPlan &&
                          getUserPlan(selectedUser) === "Enterprise")
                          ? "border-[#BDE958] bg-[#BDE958]/20 text-[#161616] ring-2 ring-[#BDE958]"
                          : "bg-[#232323] text-white border-[#232323] hover:bg-[#2A2B2D]"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
                            selectedPlan === "Enterprise" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Enterprise")
                              ? "bg-white/40 text-[#161616]"
                              : "bg-white/10 text-white"
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
                            />
                          </svg>
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            selectedPlan === "Enterprise" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Enterprise")
                              ? "text-[#161616]"
                              : "text-white"
                          }`}
                        >
                          Enterprise
                        </span>
                      </div>
                    </div>

                    {/* Developer Plan */}
                    <div
                      onClick={() => handlePlanSelect("Developer")}
                      className={`rounded-lg border p-3 cursor-pointer transition-all ${
                        selectedPlan === "Developer" ||
                        (!selectedPlan &&
                          getUserPlan(selectedUser) === "Developer")
                          ? "border-[#BDE958] bg-[#BDE958]/20 text-[#161616] ring-2 ring-[#BDE958]"
                          : "bg-[#232323] text-white border-[#232323] hover:bg-[#2A2B2D]"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
                            selectedPlan === "Developer" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Developer")
                              ? "bg-white/40 text-[#161616]"
                              : "bg-white/10 text-white"
                          }`}
                        >
                          <HiCog className="h-5 w-5" />
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            selectedPlan === "Developer" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Developer")
                              ? "text-[#161616]"
                              : "text-white"
                          }`}
                        >
                          Developer
                        </span>
                      </div>
                    </div>

                    {/* Educational Plan */}
                    <div
                      onClick={() => handlePlanSelect("Educational")}
                      className={`rounded-lg border p-3 cursor-pointer transition-all ${
                        selectedPlan === "Educational" ||
                        (!selectedPlan &&
                          getUserPlan(selectedUser) === "Educational")
                          ? "border-[#BDE958] bg-[#BDE958]/20 text-[#161616] ring-2 ring-[#BDE958]"
                          : "bg-[#232323] text-white border-[#232323] hover:bg-[#2A2B2D]"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
                            selectedPlan === "Educational" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Educational")
                              ? "bg-white/40 text-[#161616]"
                              : "bg-white/10 text-white"
                          }`}
                        >
                          <HiAcademicCap className="h-5 w-5" />
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            selectedPlan === "Educational" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Educational")
                              ? "text-[#161616]"
                              : "text-white"
                          }`}
                        >
                          Educational
                        </span>
                      </div>
                    </div>

                    {/* Trial Plan */}
                    <div
                      onClick={() => handlePlanSelect("Trial")}
                      className={`rounded-lg border p-3 cursor-pointer transition-all ${
                        selectedPlan === "Trial" ||
                        (!selectedPlan && getUserPlan(selectedUser) === "Trial")
                          ? "border-[#BDE958] bg-[#BDE958]/20 text-[#161616] ring-2 ring-[#BDE958]"
                          : "bg-[#232323] text-white border-[#232323] hover:bg-[#2A2B2D]"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
                            selectedPlan === "Trial" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Trial")
                              ? "bg-white/40 text-[#161616]"
                              : "bg-white/10 text-white"
                          }`}
                        >
                          <HiCalendar className="h-5 w-5" />
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            selectedPlan === "Trial" ||
                            (!selectedPlan &&
                              getUserPlan(selectedUser) === "Trial")
                              ? "text-[#161616]"
                              : "text-white"
                          }`}
                        >
                          Trial
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Save/Cancel buttons for plan changes */}
                  {isPlanChanged && (
                    <div className="flex justify-end mt-5 gap-3">
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleSavePlanChanges}
                        className="bg-[#BDE958] hover:bg-[#cbf176] text-[#161616] border-none"
                      >
                        <HiCheck className="mr-2 h-4 w-4" />
                        Save Changes
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        onClick={handleCancelPlanChanges}
                        className="bg-[#E8ECDA] hover:bg-[#DEE3D0] text-[#161616] border-none"
                      >
                        <HiX className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  )}

                  {/* Plan Details */}
                  <div className="mt-6 bg-[#E8ECDA] rounded-2xl p-5 border border-black/5">
                    <h5 className="text-sm font-bold text-[#161616] mb-4 uppercase">
                      Current Plan Details
                    </h5>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C6353]">
                          Plan
                        </span>
                        <span className="text-sm font-semibold text-[#161616]">
                          {selectedPlan || getUserPlan(selectedUser)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C6353]">
                          Token Limit
                        </span>
                        <span className="text-sm font-semibold text-[#161616]">
                          {(selectedPlan || getUserPlan(selectedUser)) ===
                          "Trial"
                            ? "200"
                            : "Custom"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C6353]">
                          API Rate Limit
                        </span>
                        <span className="text-sm font-semibold text-[#161616]">
                          {(selectedPlan || getUserPlan(selectedUser)) ===
                          "Enterprise"
                            ? "-- req/min"
                            : (selectedPlan || getUserPlan(selectedUser)) ===
                              "Developer"
                            ? "-- req/min"
                            : (selectedPlan || getUserPlan(selectedUser)) ===
                              "Educational"
                            ? "-- req/min"
                            : "-- req/min"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Account Action Section - Improved UI */}
            {activeSection === "accountAction" && (
              <div className="space-y-6">
                <h2 className="text-2xl md:text-4xl font-extrabold text-[#161616] mb-2">
                  Account Actions
                </h2>

                {/* Account Actions */}
                <div className="bg-[#DEE3D0] rounded-2xl p-4 md:p-6 shadow-sm">
                  <h4 className="uppercase text-[#5C6353] text-xs font-bold">ACCOUNT STATUS</h4>
                  <div className="h-px w-full bg-black/10 mt-3" />

                  <div className="bg-[#E8ECDA] rounded-2xl p-4 md:p-6 mt-4 border border-black/5">
                    <div className="flex items-center justify-between gap-6">
                      <div>
                        <div className="text-sm font-semibold text-[#161616]">Current Status</div>
                        <div className="text-xs text-[#8C9086] mt-1">
                          {selectedUser.verified ? "Active Account" : "Inactive Account"}
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          selectedUser.verified
                            ? "bg-[#BDE958]/30 hover:bg-[#BDE958]/50 text-[#161616] border-black/10"
                            : "bg-[#BDE958] hover:bg-[#cbf176] text-[#161616] border-black/10"
                        }`}
                        onClick={() =>
                          toggleUserStatus(
                            selectedUser._id,
                            selectedUser.verified,
                            { stopPropagation: () => {} }
                          )
                        }
                      >
                        {selectedUser.verified ? "Disable Account" : "Enable Account"}
                      </button>
                    </div>

                    <div className="h-px w-full bg-black/10 my-6" />

                    <div>
                      <div className="text-sm font-semibold text-red-600 mb-4">Danger Zone</div>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          className="h-11 rounded-xl bg-[#E8ECDA] hover:bg-[#DEE3D0] border border-black/10 text-[#161616] text-sm font-semibold"
                        >
                          Block Account
                        </button>
                        <button
                          type="button"
                          className="h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
                          onClick={() => {
                            setIsUserModalOpen(false);
                            setIsDeleteModalOpen(true);
                          }}
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UserProfileDetailModal;
